import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  type N26AuthState,
  type N26AuthUserInfo,
  type N26OktaAuth,
} from '@n26/okta-auth-client'
import {
  clearQueryCache,
  setUnauthorizedHandler,
} from '@/shared/api/queryClient'

type OktaContextValue = {
  oktaAuth: N26OktaAuth
  authState: N26AuthState | null
  oktaUserInfo: N26AuthUserInfo | null
  isAuthenticated: boolean
}

const OktaContext = createContext<OktaContextValue | null>(null)

type OktaProviderProps = {
  oktaAuth: N26OktaAuth
  children: ReactNode
}

export const OktaProvider = ({ oktaAuth, children }: OktaProviderProps) => {
  const [authState, setAuthState] = useState<N26AuthState | null>(() =>
    oktaAuth.n26AuthStateManager.getAuthState()
  )
  const [oktaUserInfo, setOktaUserInfo] = useState<N26AuthUserInfo | null>(null)

  useEffect(() => {
    const handler = (nextAuthState: N26AuthState) => {
      setAuthState(nextAuthState)
    }
    oktaAuth.n26AuthStateManager.subscribe(handler)
    oktaAuth.start()

    return () => {
      oktaAuth.n26AuthStateManager.unsubscribe(handler)
    }
  }, [oktaAuth])

  useEffect(() => {
    if (authState?.isAuthenticated && authState.accessToken?.accessToken) {
      oktaAuth
        .getUserInfo()
        .then(setOktaUserInfo)
        .catch(() => setOktaUserInfo(null))
    } else {
      setOktaUserInfo(null)
    }
  }, [authState, oktaAuth])

  const isAuthenticated = authState?.isAuthenticated ?? false

  // Signing out, or the session expiring, must not leave the last user's
  // cached data behind for the next one.
  const wasAuthenticated = useRef(isAuthenticated)
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) clearQueryCache()
    wasAuthenticated.current = isAuthenticated
  }, [isAuthenticated])

  // A 401 from the backend means the token is no good: drop it, which signs
  // the user out and sends the protected routes back to the login page.
  useEffect(() => {
    setUnauthorizedHandler(() => oktaAuth.n26OktaAuth.tokenManager.clear())
    return () => setUnauthorizedHandler(undefined)
  }, [oktaAuth])

  const value = useMemo<OktaContextValue>(
    () => ({ oktaAuth, authState, oktaUserInfo, isAuthenticated }),
    [oktaAuth, authState, oktaUserInfo, isAuthenticated]
  )

  return <OktaContext.Provider value={value}>{children}</OktaContext.Provider>
}

export const useOkta = (): OktaContextValue => {
  const context = useContext(OktaContext)
  if (!context) throw new Error('useOkta must be used within an OktaProvider')
  return context
}
